export type SortDirection = "asc" | "desc";

export type ListSort<Field extends string> = {
  sortBy: Field;
  sortDirection: SortDirection;
};

const CALENDAR_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export function parseList(value: string | null): string[] | undefined {
  const items = value?.split(",").filter(Boolean);
  return items?.length ? items : undefined;
}

export function parseKnownValues<Value extends string>(
  value: string | null,
  knownValues: readonly Value[],
): Value[] | undefined {
  const known = parseList(value)?.filter((item): item is Value =>
    (knownValues as readonly string[]).includes(item),
  );
  return known?.length ? known : undefined;
}

export function parseKnownValue<Value extends string>(
  value: string | null,
  knownValues: readonly Value[],
): Value | undefined {
  return (knownValues as readonly (string | null)[]).includes(value)
    ? (value as Value)
    : undefined;
}

export function parseEurosToCents(value: string | null): number | undefined {
  if (!value) return undefined;
  const euros = Number(value);
  return Number.isFinite(euros) && euros >= 0
    ? Math.round(euros * 100)
    : undefined;
}

export function parseCalendarDate(value: string | null): string | undefined {
  return value && CALENDAR_DATE_REGEX.test(value) ? value : undefined;
}

export function parseSort<Field extends string>(
  value: string | null,
  sortFields: readonly Field[],
  defaultSort: ListSort<Field>,
): ListSort<Field> {
  const [field, direction] = value?.split(":") ?? [];
  const sortBy = parseKnownValue(field ?? null, sortFields);
  const isKnownDirection = direction === "asc" || direction === "desc";
  return sortBy && isKnownDirection
    ? { sortBy, sortDirection: direction }
    : defaultSort;
}

export function parsePageIndex(value: string | null): number {
  const pageNumber = Number.parseInt(value ?? "", 10);
  return Number.isFinite(pageNumber) && pageNumber > 1 ? pageNumber - 1 : 0;
}

export function setOrDelete(
  params: URLSearchParams,
  name: string,
  value: string | undefined,
): void {
  if (value) params.set(name, value);
  else params.delete(name);
}

export function centsToEuros(cents: number | undefined): string | undefined {
  return cents === undefined ? undefined : String(cents / 100);
}

export function withListSearch(
  current: URLSearchParams,
  search: string,
): URLSearchParams {
  const params = new URLSearchParams(current);
  setOrDelete(params, "search", search.trim() || undefined);
  params.delete("page");
  return params;
}

export function withListSort<Field extends string>(
  current: URLSearchParams,
  sort: ListSort<Field>,
  defaultSort: ListSort<Field>,
): URLSearchParams {
  const params = new URLSearchParams(current);
  const isDefaultSort =
    sort.sortBy === defaultSort.sortBy &&
    sort.sortDirection === defaultSort.sortDirection;
  setOrDelete(
    params,
    "sort",
    isDefaultSort ? undefined : `${sort.sortBy}:${sort.sortDirection}`,
  );
  params.delete("page");
  return params;
}

export function withListPage(
  current: URLSearchParams,
  pageIndex: number,
): URLSearchParams {
  const params = new URLSearchParams(current);
  setOrDelete(
    params,
    "page",
    pageIndex > 0 ? String(pageIndex + 1) : undefined,
  );
  return params;
}

export function hasActiveListFilters(filters: object): boolean {
  return Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : !!value || value === 0,
  );
}
